import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getProject } from '../services/webService';
import { GLOBAL } from '../services/global';
import { translate } from '../utils/translations';
import { projectPublishing } from '../utils/projectPublishing';
import { safeUrl } from '../utils/safeUrl';
import './Project.css';

// Devuelve true cuando el enlace embebido es un juego/web app (iframe grande)
// y false cuando es un vídeo (YouTube, Vimeo, etc.) que usa el iframe pequeño.
function isGameUrl(url) {
  if (!url) return false;
  return !/youtube|youtu\.be|vimeo|dailymotion|\.mp4|\.webm|\.ogg/i.test(url);
}

function Project() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [projectSelected, setProjectSelected] = useState(null);
  const [isload, setIsload] = useState(false);

  useEffect(() => {
    const project = getProject(id);
    if (!project) {
      navigate('/home');
      return;
    }

    project.publishing.forEach(publish => {
      if (publish.publishing === GLOBAL.confPublishingWeb.publishing && publish.urlPublishing === 'game')
        publish.urlPublishing = window.location.href + '/game';
      if (publish.publishing === GLOBAL.confPublishingWeb.publishing && publish.urlPublishing === 'maps')
        publish.urlPublishing = window.location.href + '/maps';
    });

    setProjectSelected(project);
    setIsload(true);
  }, [id, navigate]);

  if (!isload || !projectSelected) {
    return <div className="container-custom"><p>Loading...</p></div>;
  }

  return (
    <div className="container-custom">
      <div className="row">
        <div className="col-lg-12">
          <h1 className="page-header">
            <img src={projectSelected.iconImage} alt="" /> {translate(projectSelected.name)}
            <span style={{ fontSize: '65%', color: '#777' }}> {projectPublishing(projectSelected)}</span>
          </h1>
          <ol className="breadcrumb">
            <li><Link to="/home">Home</Link></li>
            &nbsp;/&nbsp;
            <li className="active"> {translate(projectSelected.name)}</li>
          </ol>
        </div>
      </div>

      <div className="row">
        <div className="col-md-6">
          {projectSelected.images && projectSelected.images.length > 0 && (
            <CarouselView images={projectSelected.images} isVertical={projectSelected.isImageVertical} />
          )}
        </div>

        <div className="col-md-6 page-description">
          <h3>Description</h3>
          <div dangerouslySetInnerHTML={{ __html: translate(projectSelected.description) }}></div>
          <br />
          {projectSelected.details && (
            <div dangerouslySetInnerHTML={{ __html: translate(projectSelected.details) }}></div>
          )}
        </div>
      </div>

      {projectSelected.urlVideo && (
        <>
          <hr />
          <div className="row">
            <div className="col-lg-12">
              {isGameUrl(projectSelected.urlVideo) ? (
                <iframe
                  className="game-container-iframe"
                  src={safeUrl(projectSelected.urlVideo)}
                  title="game"
                  frameBorder="0"
                  allowFullScreen
                  style={{ width: '100%', height: '780px' }}
                ></iframe>
              ) : (
                <iframe className="video-container-iframe" height="315" width="560"
                  src={safeUrl(projectSelected.urlVideo)} allowFullScreen title="video" frameBorder="0">
                </iframe>
              )}
            </div>
          </div>
        </>
      )}

      {projectSelected.publishing && projectSelected.publishing.length > 0 && (
        <>
          <div className="row">
            <div className="col-lg-12">
              <h3 className="page-header">Available</h3>
            </div>
          </div>

          <div className="row">
            <div className="col-lg-12">
              <div className="d-flex flex-wrap justify-content-center" style={{ gap: '50px' }}>
                {projectSelected.publishing.map((publish, index) => (
                  <div key={index} className="card-item">
                    <a title={publish.publishing} href={publish.urlPublishing} target="_blank" rel="noopener noreferrer">
                      <img src={publish.imagePublishing} alt="" className="img-responsive" height="70px" />
                    </a>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      <div className="row">
        <div className="col-lg-12">
          <h2 className="page-header">Our Team</h2>
          <div className="d-flex flex-wrap justify-content-center" style={{ gap: '50px' }}>
            {projectSelected.team.map((developer, index) => (
              <div key={index} className="card-item-custom">
                {developer.imageDev ? (
                  <img src={developer.imageDev} alt="" className="img-responsive-photo" />
                ) : (
                  <img src="/assets/images/team/avatar.png" alt="" className="img-responsive-photo" />
                )}
                <div className="caption">
                  <h4>{developer.name}<br />
                    <span style={{ fontSize: '65%', color: '#777' }}>{developer.rol}</span>
                  </h4>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Reusable carousel component with auto-play every 3 seconds
function CarouselView({ images, isVertical }) {
  const [activeIndex, setActiveIndex] = useState(0);

  // Contador para reiniciar el auto-play cuando el usuario navega manualmente
  const [resetKey, setResetKey] = useState(0);

  const prevSlide = () => {
    setActiveIndex(prev => (prev === 0 ? images.length - 1 : prev - 1));
    setResetKey(k => k + 1);
  };

  const nextSlide = () => {
    setActiveIndex(prev => (prev === images.length - 1 ? 0 : prev + 1));
    setResetKey(k => k + 1);
  };

  // Auto-advance every 3 seconds (se reinicia al cambiar resetKey)
  useEffect(() => {
    if (images.length <= 1) return;
    const interval = setInterval(() => {
      setActiveIndex(prev => (prev === images.length - 1 ? 0 : prev + 1));
    }, 3000);
    return () => clearInterval(interval);
  }, [images.length, resetKey]);

  return (
    <div style={{ maxWidth: isVertical ? '300px' : '768px', margin: '0 auto', position: 'relative' }}>
      <div style={{ overflow: 'hidden', borderRadius: '8px' }}>
        <img
          className="d-block img-fluid"
          src={images[activeIndex]}
          alt={`Slide ${activeIndex + 1}`}
          style={{ maxHeight: '500px', margin: '0 auto', width: '100%', objectFit: 'contain' }}
        />
      </div>
      {images.length > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '10px', gap: '15px' }}>
          <button onClick={prevSlide} style={{ background: '#333', color: '#fff', border: 'none', borderRadius: '50%', width: '36px', height: '36px', cursor: 'pointer', fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            &#8249;
          </button>
          <span style={{ color: '#666', fontSize: '14px' }}>
            {activeIndex + 1} / {images.length}
          </span>
          <button onClick={nextSlide} style={{ background: '#333', color: '#fff', border: 'none', borderRadius: '50%', width: '36px', height: '36px', cursor: 'pointer', fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            &#8250;
          </button>
        </div>
      )}
    </div>
  );
}

export default Project;
